import React from 'react';
import { motion } from 'framer-motion';
import { usePublications } from '../../contexts/PublicationContext';
import { useProfile } from '../../contexts/ProfileContext';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { PublicationsPageSkeleton } from '../../components/skeletons/PageSkeletons';

const PublicationsPage: React.FC = () => {
  const { publications, loading, error } = usePublications();
  const { profile, loading: profileLoading } = useProfile();

  // Animation variants (unused but kept for potential future use)
  // const container = {
  //   hidden: { opacity: 0 },
  //   show: {
  //     opacity: 1,
  //     transition: {
  //       staggerChildren: 0.1
  //     }
  //   }
  // };

  // const item = {
  //   hidden: { opacity: 0, y: 20 },
  //   show: { opacity: 1, y: 0 }
  // };

  // Get research interests from profile
  const researchInterests = profile?.researchInterests || [
    {
      title: "Machine Learning",
      description: "Exploring novel applications of machine learning algorithms to solve real-world problems, with a focus on practical implementations and performance optimization."
    },
    {
      title: "Natural Language Processing",
      description: "Developing and implementing NLP solutions for Bengali language, focusing on sentiment analysis, hate speech detection, and language understanding."
    },
    {
      title: "Smart Grid & IoT",
      description: "Applying data science and machine learning techniques to improve power system operations, load forecasting, and IoT-based monitoring solutions."
    }
  ];

  if (loading || profileLoading) {
    return <PublicationsPageSkeleton />;
  }

  if (error) {
    return (
      <div className="container py-16 lg:pt-[var(--page-top)]">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-red-800 dark:text-red-200">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Sort by year (newest first)
  const sortedPublications = [...publications].sort((a, b) => {
    const yearA = parseInt(a.year);
    const yearB = parseInt(b.year);
    return yearB - yearA;
  });

  // Get featured publications
  const featuredPublications = sortedPublications.filter(pub => pub.featured);

  // Count publications by year
  const publicationsByYear = sortedPublications.reduce((acc, pub) => {
    acc[pub.year] = (acc[pub.year] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Count publications by keyword
  const publicationsByKeyword = sortedPublications.reduce((acc, pub) => {
    if (pub.keywords && pub.keywords.length) {
      pub.keywords.forEach(keyword => {
        acc[keyword] = (acc[keyword] || 0) + 1;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  // Get top keywords
  const topKeywords = Object.entries(publicationsByKeyword)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  return (
    <div className="container py-16 lg:pt-[var(--page-top)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="section-title">Publications</h1>
        
        {/* Enhanced Research Summary */}
        <motion.div 
          className="relative mb-16"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-blue-500/10 to-purple-500/10 dark:from-primary-900/20 dark:via-blue-900/20 dark:to-purple-900/20 rounded-3xl transform rotate-1"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-primary-500/10 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-primary-900/20 rounded-3xl transform -rotate-1"></div>
          
          {/* Main content */}
          <div className="relative bg-white dark:bg-gray-800 p-12 md:p-12 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
            {/* Decorative research icon */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-primary-500 to-blue-500 p-4 rounded-full shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>

            <div className="text-center mt-2">
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  Research Overview
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-blue-500 mx-auto rounded-full"></div>
              </div>
              
              <blockquote className="relative">
                {/* Large decorative quotes */}
                <span className="absolute -top-8 -left-4 text-8xl text-primary-200 dark:text-primary-800 font-serif leading-none select-none">"</span>
                <span className="absolute -bottom-16 -right-4 text-8xl text-primary-200 dark:text-primary-800 font-serif leading-none select-none">"</span>
                
                <p className="text-xl md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-5xl mx-auto font-light relative z-10 italic">
                  {profile.researchSummary || "My research focuses on machine learning applications, particularly in the areas of natural language processing and power systems. Below are my recent publications."}
                </p>
              </blockquote>

              {/* Signature or accent line */}
              <div className="mt-12 flex items-center justify-center space-x-4">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary-500"></div>
                <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"></div>
                <div className="h-px w-16 bg-gradient-to-r from-primary-500 to-transparent"></div>
              </div>
            </div>

            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-blue-50/50 dark:from-primary-900/10 dark:via-transparent dark:to-blue-900/10 rounded-3xl pointer-events-none"></div>
          </div>
        </motion.div>
        
        {/* Academic profiles and stats section */}
        <div className="max-w-3xl mx-auto mb-12">
          {/* Academic profiles */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {profile.socialLinks?.googleScholar && (
              <a 
                href={profile.socialLinks.googleScholar} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/>
                </svg>
                Google Scholar Profile
              </a>
            )}
            
            {profile.socialLinks?.researchGate && (
              <a 
                href={profile.socialLinks.researchGate} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.586 0c-.818 0-1.508.19-2.073.565-.563.377-.97.936-1.213 1.68a3.193 3.193 0 0 0-.112.437 8.365 8.365 0 0 0-.078.53 9 9 0 0 0-.05.727c-.01.282-.013.621-.013 1.016a31.121 31.121 0 0 0 .014 1.017 9 9 0 0 0 .05.727 7.946 7.946 0 0 0 .077.53h-.005a3.334 3.334 0 0 0 .113.438c.245.743.65 1.303 1.214 1.68.565.376 1.256.564 2.075.564.8 0 1.536-.213 2.105-.603.57-.39.94-.916 1.175-1.65.076-.235.135-.558.177-.93.043-.372.064-.938.064-1.696 0-.757-.021-1.323-.064-1.696-.042-.371-.101-.695-.177-.93-.234-.734-.602-1.26-1.17-1.648-.569-.39-1.298-.583-2.11-.583zM7.996.006c-.857 0-1.635.182-2.33.565-.697.384-1.252.988-1.683 1.808-.82.156-.146.33-.205.519a6.82 6.82 0 0 0-.147.585 7.075 7.075 0 0 0-.09.678 8.922 8.922 0 0 0-.017.792c-.004.262 0 .568.008.903.01.334.022.703.041 1.103l-.002.002a17.815 17.815 0 0 0 .141 1.489c.059.433.123.833.191 1.186.214 1.084.5 2.172.875 3.14a10.88 10.88 0 0 0 1.148 2.284 5.224 5.224 0 0 0 1.418 1.488c.76.545 1.53.814 2.312.814a3.658 3.658 0 0 0 1.623-.378 3.453 3.453 0 0 0 1.278-1.012 6.448 6.448 0 0 0 .878-1.569c.237-.595.444-1.228.608-1.908.164-.68.284-1.328.357-1.926.073-.598.123-1.153.141-1.648.018-.495.027-.914.027-1.25 0-.468-.009-.86-.027-1.185a9.578 9.578 0 0 0-.09-.865 3.659 3.659 0 0 0-.233-.825c-.142-.329-.356-.6-.636-.812-.28-.212-.655-.33-1.142-.378-.596-.044-1.142.088-1.644.378-.503.29-.943.726-1.319 1.302.066-1.259.018-2.145-.103-2.677-.12-.533-.295-.886-.521-1.055-.227-.17-.506-.254-.836-.254z"/>
                </svg>
                ResearchGate Profile
              </a>
            )}
            
            {profile.socialLinks?.orcid && (
              <a 
                href={profile.socialLinks.orcid} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.95.95 0 0 1-.947-.947c0-.525.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.025 0 2.578-2.016 5.025-5.325 5.025h-3.919V7.416zm1.444 1.303v7.444h2.297c3.272 0 4.022-2.484 4.022-3.722 0-2.016-1.284-3.722-4.097-3.722h-2.222z"/>
                </svg>
                ORCID ID
              </a>
            )}
          </div>
          
          {/* Publication statistics */}
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm flex items-center">
              <span className="text-gray-600 dark:text-gray-400 text-sm mr-2">Total publications:</span>
              <span className="font-semibold text-primary-600 dark:text-primary-400">{publications.length}</span>
            </div>
            
            {Object.keys(publicationsByYear).length > 0 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm flex items-center">
                <span className="text-gray-600 dark:text-gray-400 text-sm mr-2">Latest:</span>
                <span className="font-semibold text-primary-600 dark:text-primary-400">{Math.max(...Object.keys(publicationsByYear).map(y => parseInt(y)))}</span>
              </div>
            )}
          </div>
        </div>

        {/* Research Interests */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Research Interests</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {researchInterests.map((interest, idx) => (
              <motion.div
                key={idx}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                  {interest.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {interest.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Featured Publications */}
        {featuredPublications.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Featured Publications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredPublications.map(publication => (
                <motion.div
                  key={publication.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  {publication.coverImage && (
                    <div className="w-full h-48 overflow-hidden">
                      <img 
                        src={publication.coverImage} 
                        alt={publication.title} 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                      {publication.title}
                    </h3>
                    
                    <div className="mb-4">
                      <p className="text-gray-600 dark:text-gray-400 italic">
                        {publication.authors.join(', ')}
                      </p>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">
                        {publication.conference || publication.journal}, {publication.year}
                        {publication.location && `, ${publication.location}`}
                      </p>
                      {publication.doi && (
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                          DOI: {publication.doi}
                        </p>
                      )}
                    </div>
                    
                    {publication.abstract && (
                      <div className="mb-6">
                        <p className="text-gray-600 dark:text-gray-400 line-clamp-3">
                          {publication.abstract}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      {publication.link && (
                        <a
                          href={publication.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <span>View Publication</span>
                          <ArrowTopRightOnSquareIcon className="ml-1 w-4 h-4" />
                        </a>
                      )}
                      
                      {publication.doi && !publication.link && (
                        <a
                          href={`https://doi.org/${publication.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <span>View DOI</span>
                          <ArrowTopRightOnSquareIcon className="ml-1 w-4 h-4" />
                        </a>
                      )}
                      
                      {publication.keywords && publication.keywords.length > 0 && (
                        <div className="flex flex-wrap justify-end">
                          {publication.keywords.slice(0, 3).map((keyword, idx) => (
                            <span key={idx} className="ml-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded-full text-gray-600 dark:text-gray-300">
                              {keyword}
                            </span>
                          ))}
                          {publication.keywords.length > 3 && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">+{publication.keywords.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Research Keywords */}
        {topKeywords.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4 text-center">Research Topics</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {topKeywords.map(([keyword, count]) => (
                <div key={keyword} className="bg-gray-100 dark:bg-gray-700 rounded-full px-4 py-2 flex items-center">
                  <span className="text-gray-700 dark:text-gray-300">{keyword}</span>
                  <span className="ml-2 bg-primary-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Publications */}
        <div className="space-y-10">
          <h2 className="text-2xl font-bold mb-6 text-center">All Publications</h2>
          {sortedPublications.length > 0 ? (
            sortedPublications.map(publication => (
              <motion.div
                key={publication.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                    {publication.title}
                  </h2>
                  
                  <div className="mb-4">
                    <p className="text-gray-600 dark:text-gray-400 italic">
                      {publication.authors.join(', ')}
                    </p>
                    <p className="text-primary-600 dark:text-primary-400">
                      {publication.conference || publication.journal}, {publication.year}
                    </p>
                    {publication.location && (
                      <p className="text-gray-500 dark:text-gray-500 text-sm">
                        {publication.location}
                      </p>
                    )}
                    {publication.doi && (
                      <p className="text-gray-500 dark:text-gray-500 text-sm mt-1">
                        DOI: <a 
                          href={`https://doi.org/${publication.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {publication.doi}
                        </a>
                      </p>
                    )}
                  </div>
                  
                  {publication.abstract && (
                    <div className="mb-6">
                      <h3 className="font-medium mb-2 text-gray-700 dark:text-gray-300">Abstract</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {publication.abstract}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center justify-between">
                    <div className="mb-2 md:mb-0">
                      {publication.keywords && publication.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {publication.keywords.map((keyword, idx) => (
                            <span key={idx} className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-xs text-gray-600 dark:text-gray-300">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {publication.link && (
                        <a
                          href={publication.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center"
                        >
                          <span>View Publication</span>
                          <ArrowTopRightOnSquareIcon className="ml-1 w-4 h-4" />
                        </a>
                      )}
                      
                      {publication.pdfUrl && (
                        <a
                          href={publication.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center"
                        >
                          <span>Download PDF</span>
                          <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No publications available yet.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PublicationsPage; 